import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useConfirm } from "../context/ConfirmContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Calendar, CheckCircle2, Sparkles, Megaphone, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function monthLabel(s) {
  if (!s) return "—";
  const [y, m] = s.split("-");
  const names = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

export default function EditionAdmin() {
  const confirm = useConfirm();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [publishing, setPublishing] = useState(false);

  const load = async () => {
    try {
      const [cur, hist] = await Promise.all([
        api.get("/admin/editions/current"),
        api.get("/admin/editions"),
      ]);
      setCurrent(cur.data);
      setHistory(hist.data || []);
    } catch (err) {
      console.warn("EditionAdmin: failed to load editions", err?.response?.data || err?.message);
    }
  };

  useEffect(() => { load(); }, []);

  const publish = async () => {
    if (!await confirm(
      `¿Publicar el nuevo directorio del mes de ${monthLabel(current.month)}?\n\n` +
      "Esto:\n" +
      "• Cierra oficialmente la ventana de edición de este mes.\n" +
      "• Manda una notificación a TODOS los comercios afiliados.\n" +
      "• Queda registrado como la edición oficial del mes.\n\n" +
      "Esta acción es idempotente: solo puede ejecutarse una vez por mes.",
      { title: `Publicar edición ${monthLabel(current.month)}` }
    )) return;
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/editions/publish");
      toast.success(`¡Directorio publicado! ${data.notifications_sent} comercios notificados.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al publicar");
    } finally { setPublishing(false); }
  };

  if (!current) return <div className="text-muted-foreground">Cargando…</div>;

  const today = current.today;
  const isPastDay6 = today >= current.edition_day;
  const alreadyPublished = current.is_published;
  const stats = current.stats || {};

  return (
    <div className="space-y-5">
      {/* Hero card */}
      <div className="bg-white border border-border rounded-2xl p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#4C1D95] grid place-items-center">
              <Megaphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-display text-2xl tracking-tight">Edición del directorio</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Mes en curso: <strong>{monthLabel(current.month)}</strong> · Hoy es día <strong>{today}</strong>
              </p>
            </div>
          </div>
          {alreadyPublished ? (
            <Badge className="bg-emerald-500 text-white px-4 py-2" data-testid="edition-status">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Publicada
            </Badge>
          ) : isPastDay6 ? (
            <Badge className="bg-amber-300 text-amber-900 px-4 py-2" data-testid="edition-status">
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Falta publicar
            </Badge>
          ) : (
            <Badge variant="outline" className="px-4 py-2" data-testid="edition-status">
              Pendiente de publicar
            </Badge>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <Stat label="Comercios activos" value={stats.active_businesses} />
          <Stat label="Nuevos este mes" value={stats.first_month_businesses} accent="violet" />
          <Stat label="Recurrentes" value={stats.existing_with_history} />
          <Stat label="Anuncios editados" value={stats.ads_updated_this_month} accent="amber" />
        </div>

        {/* Instructions panel */}
        <div className="mt-6 bg-[#F5F3FF] border border-[#DDD6FE] rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-[#7C3AED] mt-0.5 shrink-0" />
            <div className="text-sm leading-relaxed text-[#3B1565]">
              <div className="font-display text-lg text-[#5B21B6]">Instrucciones para publicar la edición</div>
              <ol className="mt-3 space-y-2 list-decimal pl-5">
                <li><strong>Día 1 al 5:</strong> Los comercios afiliados (segundo mes en adelante) actualizan sus anuncios desde su panel. Tú no haces nada aún.</li>
                <li><strong>Día 6:</strong> Aprieta el botón <strong>“Publicar nuevo directorio”</strong> abajo. Esto cierra el corte oficial y manda una notificación a todos los comercios.</li>
                <li><strong>Día 6 al 5 del próximo mes:</strong> El directorio queda congelado para los recurrentes. Los nuevos comercios que paguen siguen entrando al instante.</li>
                <li>Si algún recurrente no actualizó su anuncio antes del corte, su anuncio anterior se mantiene automáticamente (no necesitas hacer nada).</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Action button */}
        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-muted-foreground">
            <Calendar className="inline w-4 h-4 mr-1" />
            La acción es <strong>idempotente</strong>: solo puede ejecutarse una vez por mes.
          </div>
          <Button
            onClick={publish}
            disabled={alreadyPublished || publishing}
            className={`rounded-full px-6 py-5 ${alreadyPublished ? "" : "btn-primary"}`}
            data-testid="publish-edition-btn"
          >
            <Megaphone className="w-4 h-4 mr-2" />
            {alreadyPublished
              ? `Ya publicada · ${(current.published_at || "").slice(0, 10)}`
              : publishing
                ? "Publicando…"
                : `Publicar nuevo directorio de ${monthLabel(current.month)}`}
          </Button>
        </div>
      </div>

      {/* History */}
      <div className="bg-white border border-border rounded-2xl p-6">
        <h3 className="font-display text-xl tracking-tight">Historial de ediciones</h3>
        {history.length === 0 ? (
          <div className="text-sm text-muted-foreground mt-3 py-6 text-center border-2 border-dashed border-border rounded-xl" data-testid="edition-history-empty">
            Aún no has publicado ninguna edición.
          </div>
        ) : (
          <table className="w-full text-sm mt-4">
            <thead className="text-left text-muted-foreground border-b border-border">
              <tr><th className="py-2">Mes</th><th>Publicada el</th><th>Comercios</th><th>Notificaciones</th></tr>
            </thead>
            <tbody>
              {history.map((e) => (
                <tr key={e.month} className="border-b border-border" data-testid={`edition-history-${e.month}`}>
                  <td className="py-3 font-medium">{monthLabel(e.month)}</td>
                  <td className="text-xs">{(e.published_at || "").slice(0, 10)}</td>
                  <td><Badge variant="outline">{e.businesses_promoted}</Badge></td>
                  <td className="text-xs">{e.notifications_sent} enviadas</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, accent }) => {
  const cls = accent === "violet"
    ? "bg-[#F5F3FF] border-[#DDD6FE] text-[#5B21B6]"
    : accent === "amber"
      ? "bg-amber-50 border-amber-200 text-amber-900"
      : "bg-white border-border";
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <div className="overline text-[10px] text-muted-foreground">{label}</div>
      <div className="font-display text-3xl mt-1">{value ?? 0}</div>
    </div>
  );
};
